/**
 * Fetch rows across multiple pages for export.
 *
 * The backend Lambda cannot return large page_size in a single response
 * (>6MB blows the sync-invoke limit → HTTP 413). So we page in small chunks
 * and concatenate up to `maxRows`.
 */

export const EXPORT_PAGE_SIZE = 500;

export async function fetchAllForExport<T>(
    fetchPage: (
        page: number,
        pageSize: number,
    ) => Promise<{ rows: T[]; hasMore?: boolean; totalPages?: number }>,
    maxRows: number,
    pageSize: number = EXPORT_PAGE_SIZE,
): Promise<T[]> {
    const hardCap = 20000; // ponytail: runaway guard, raise if exports ever exceed this
    const limit = Math.min(maxRows || hardCap, hardCap);
    const all: T[] = [];

    for (let page = 1; all.length < limit; page++) {
        const { rows, hasMore, totalPages } = await fetchPage(page, pageSize);
        if (!rows || rows.length === 0) break;
        all.push(...rows);
        if (hasMore === false) break; // backend says this is the last page
        if (hasMore === undefined && rows.length < pageSize) break; // fallback: short page = last
        if (totalPages && page >= totalPages) break;
    }

    return all.slice(0, limit);
}
